import { MessageDto, UserDto } from '@act/shared/data-transfer-objects';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useToast } from 'react-components/app/ReusableToast';
import useMessageDialog from 'react-components/form-dialog/hooks/useMessageDialog';
import { TrackingProvider, useTracking } from 'react-components/tracking';
import {
  deleteMessage,
  sendScheduledMessage,
} from 'redux/slices/messagesSlice';
import { useAppDispatch } from 'redux/store-hooks';
import MessageItem from './MessageItem';
interface ScheduledMessageDrawerProps {
  selectedMember: UserDto;
  open: boolean;
  onClose: () => void;
  scheduledMessages: MessageDto[];
}

export default function ScheduledMessageDrawer({
  open,
  onClose,
  selectedMember,
  scheduledMessages,
}: ScheduledMessageDrawerProps) {
  const { generateTrackingProps } = useTracking();
  const openMessageDialog = useMessageDialog();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const handleEditMessage = (message: MessageDto) => {
    void openMessageDialog({
      user: selectedMember,
      message,
    });
  };

  const handleDeleteMessage = async (message: MessageDto) => {
    const result = await dispatch(
      deleteMessage({
        params: { messageId: message.id },
      })
    );
    if (deleteMessage.fulfilled.match(result)) {
      toast('Scheduled message deleted successfully', 'success');
    } else if (deleteMessage.rejected.match(result)) {
      toast('Failed to delete scheduled message', 'error');
    }
  };
 
  const handleSendMessage = async (message: MessageDto) => {
    const result = await dispatch(
      sendScheduledMessage({
        params: { messageId: message.id },
      })
    );
    if (sendScheduledMessage.fulfilled.match(result)) {
      toast('Message sent successfully', 'success');
    } else if (sendScheduledMessage.rejected.match(result)) {
      toast('Failed to send message', 'error');
    }
  };

  return (
    <TrackingProvider contextName="scheduled-message-drawer">
      <Drawer
        open={open}
        onClose={() => onClose()}
        anchor="right"
        PaperProps={{
          sx: {
            marginTop: '50px',
            width: '40%',
            padding: '24px',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '24px',
            marginBottom: '24px',
          }}
        >
          <Typography variant="headlineMedium">
            Scheduled texts to {selectedMember?.firstName}
          </Typography>
          <IconButton
            onClick={() => onClose()}
            {...generateTrackingProps('close-drawer-button')}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: '50px',
          }}
        >
          {scheduledMessages?.length > 0 ? (
            scheduledMessages?.map(message => (
              <MessageItem
                key={message.id}
                message={message}
                onEdit={handleEditMessage}
                onDelete={handleDeleteMessage}
                onSend={handleSendMessage}
              />
            ))
          ) : (
            <Typography variant="bodyMedium">No scheduled messages</Typography>
          )}
        </Box>
      </Drawer>
    </TrackingProvider>
  );
}
